import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {catchError, finalize, map, Observable, of, switchMap, tap, throwError} from 'rxjs';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {environment} from '../../../../environments/environment';
import {isPlatformBrowser} from '@angular/common';
import {TokenStorageService} from '../token-storage.service';
import {ServiceTokenService} from '../service-token.service';
import {SubscribersService} from '../subscribers.service';
import {FrontendUserVerificationCodeModel, ResetPasswordModel, ResponseModel, SessionModel, UserModel,} from '@coreModels';
import {ApiConstants, STORAGE} from '@coreConstant';
import {ToastService} from '../toast.service';

/**
 * Credentials posted to the login endpoint. `scope`/`client` are stamped on by
 * {@link AuthService.login} itself, so callers only supply the user's credentials.
 * No client_secret here — the Node BFF injects it server-side.
 */
type LoginRequest = {
  username: string;
  password: string;
  scope?: string;
  client?: string;
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly CLIENT_ID: string = environment.clientId;
  private readonly GRANT_TYPE: string = environment.grantType;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    private tokenStorageService: TokenStorageService,
    private serviceTokenService: ServiceTokenService,
    private subscriberService: SubscribersService,
    private toastService: ToastService
  ) {}

  /**
   * App bootstrap: purge any legacy localStorage token/user (one-time cleanup) and rebuild auth
   * state from the /me endpoint (the access token is now an HttpOnly cookie the browser sends
   * automatically). Runs browser-side only. Always resolves so it can gate APP_INITIALIZER.
   */
  init(): Observable<string | null> {
    if (!isPlatformBrowser(this.platformId)) {
      return of(null);
    }

    // One-time cleanup of stale localStorage credentials from the pre-cookie implementation.
    localStorage.removeItem(STORAGE.TOKEN_KEY);
    localStorage.removeItem(STORAGE.USER_KEY);

    // Guests (never signed in here, or signed out) have no cookie to rebuild from, so probing /me
    // would only produce a guaranteed 401 in the console, e.g. on shared quote-invoice links.
    if (!this.tokenStorageService.hasSessionHint()) {
      return of(null);
    }

    // Rebuild the user from /me, then fetch the in-memory service token for cross-host (Node API) calls.
    return this.refreshUser().pipe(
      switchMap(() => this.serviceTokenService.refresh()),
      catchError((err: unknown) => {
        // The cookie is gone or expired: stop probing on future loads. Any other failure (offline,
        // 5xx, timeout) keeps the hint so the session can still be recovered on the next load.
        if (err instanceof HttpErrorResponse && err.status === 401) {
          this.tokenStorageService.clearSessionHint();
        }
        return of(null);
      })
    );
  }

  /**
   * Fetch the current user from /me and store it in memory (source of truth for auth state).
   */
  refreshUser(): Observable<UserModel> {
    return this.http.get<ResponseModel>(ApiConstants.USER_DETAIL_URL).pipe(
      map((response) => (response.data || response?.body?.data) as UserModel),
      tap((user: UserModel) => this.tokenStorageService.setUser(user))
    );
  }

  /**
   * Fetch the in-memory service token used to authenticate cross-host (Node API) requests.
   */
  refreshServiceToken(): Observable<string | null> {
    return this.serviceTokenService.refresh();
  }

  /**
   * Server-side logout: revoke the token behind the cookie and clear the cookies, then reset
   * client auth state regardless of the network result.
   */
  logout(): Observable<unknown> {
    return this.http.post<unknown>(ApiConstants.LOGOUT_URL, {}).pipe(
      catchError((err: any) => err?.status && err.status < 500
        ? throwError(() => err)
        : this.http.post<unknown>(ApiConstants.LOGOUT_URL, {})),
      catchError(() => of(null)),
      finalize(() => {
        this.tokenStorageService.clearUser();
        this.tokenStorageService.clearSessionHint();
        this.serviceTokenService.clear();
        this.subscriberService.setUserLoggedInStatus(false);
      })
    );
  }

  login(requestModel: LoginRequest): Observable<SessionModel> {
    // Client secret is injected server-side by the Node BFF (C1/F1) — the browser
    // only declares which app it is. Never send a client_secret from the SPA bundle.
    requestModel.scope = '';
    requestModel.client = 'website';
    return this.http.post(ApiConstants.LOGIN_URL, requestModel).pipe(
      catchError((err: any) => err?.status && err.status < 500
        ? throwError(() => err)
        : this.http.post(ApiConstants.LOGIN_URL, requestModel))
    );
  }

  getLoginUserDetail(): Observable<UserModel> {
    return this.http.get<ResponseModel>(ApiConstants.USER_DETAIL_URL).pipe(
      map((response) => (response.data || response?.body.data) as UserModel),
      catchError((err: unknown) => {
        this.toastService.showError(err);
        return throwError(() => err);
      })
    );
  }

  sendVerificationcode(
    data: Partial<FrontendUserVerificationCodeModel>
  ): Observable<ResponseModel> {
    return this.http.post<ResponseModel>(ApiConstants.USER_VERIFICATION_CODE_URL, data).pipe(
      map((response) => response.data as ResponseModel),
      catchError((err: unknown) => {
        // this.toastService.showError(err);
        return throwError(() => err);
      })
    );
  }

  reSendVerificationCode(
    data: Partial<FrontendUserVerificationCodeModel>
  ): Observable<ResponseModel> {
    return this.http
      .post<ResponseModel>(ApiConstants.USER_RESEND_VERIFICATION_CODE_URL, data)
      .pipe(
        map((response) => response.data as ResponseModel),
        catchError((err: unknown) => {
          this.toastService.showError(err);
          return throwError(() => err);
        })
      );
  }

  verifiyCode(
    data: Partial<FrontendUserVerificationCodeModel>
  ): Observable<FrontendUserVerificationCodeModel> {
    return this.http.post<ResponseModel>(ApiConstants.USER_VERIFY_CODE_URL, data).pipe(
      map((response) => response.data as FrontendUserVerificationCodeModel),
      catchError((err: unknown) => {
        return throwError(() => err);
      })
    );
  }

  loginWithFacebook(requestModel: Record<string, unknown>): Observable<SessionModel> {
    return this.http
      .post<SessionModel>(ApiConstants.LOGIN_WITH_FACEBOOK_URL, requestModel)
      .pipe(
        map((response) => response),
        catchError((err: unknown) => {
          return throwError(() => err);
        })
      );
  }

  loginWithGoogle(requestModel: Record<string, unknown>): Observable<SessionModel> {
    return this.http
      .post<SessionModel>(ApiConstants.LOGIN_WITH_GOOGLE_URL, requestModel)
      .pipe(
        map((response) => response),
        catchError((err: unknown) => {
          return throwError(() => err);
        })
      );
  }

  signUp(requestModel: UserModel | Record<string, unknown>): Observable<UserModel> {
    return this.http.post<ResponseModel>(ApiConstants.REGISTER_URL, requestModel).pipe(
      map((response) => response.data as UserModel),
      catchError((err: unknown) => {
        return throwError(() => err);
      })
    );
  }

  forgotPassword(forgotPasswordModel: Record<string, unknown>): Observable<ResponseModel> {
    return this.http
      .post<ResponseModel>(ApiConstants.USER_FORGOT_PASSWORD_URL, forgotPasswordModel)
      .pipe(
        map((response) => response.data as ResponseModel),
        catchError((err: unknown) => {
          return throwError(() => err);
        })
      );
  }

  changePassword(changePasswordModel: Record<string, unknown>): Observable<ResponseModel> {
    return this.http
      .post<ResponseModel>(ApiConstants.USER_CHANGE_PASSWORD_URL, changePasswordModel)
      .pipe(
        map((response) => response.data as ResponseModel),
        catchError((err: unknown) => {
          return throwError(() => err);
        })
      );
  }

  /**
   * Is this reset link still usable? Consumes nothing — it returns the same verdict the real reset
   * would, so the screen can say "this link has already been used" instead of accepting a new
   * password and only then failing.
   */
  validateResetToken(clientId: string, token: string): Observable<ResponseModel> {
    return this.http
      .post<ResponseModel>(ApiConstants.USER_VALIDATE_RESET_TOKEN_URL, {
        client_id: clientId,
        password_reset_token: token,
      })
      .pipe(catchError((err: unknown) => throwError(() => err)));
  }

  resetPassword(resetPasswordModel: ResetPasswordModel): Observable<ResponseModel> {
    return this.http
      .post<ResponseModel>(ApiConstants.USER_RESET_PASSWORD_URL, resetPasswordModel)
      .pipe(
        map((response) => response.data as ResponseModel),
        catchError((err: unknown) => {
          return throwError(() => err);
        })
      );
  }

  isUserAuthenticated(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      // Auth state derives from the in-memory user rebuilt from /me, not from a stored token.
      return this.tokenStorageService.checkUserLoggedIn();
    }
    // SSR: no browser session — treated as not authenticated (same falsy behaviour as before).
    return false;
  }

  isrCeditEarnDisabled(): string | null {
    // Now it's safe to access localStorage
    return this.tokenStorageService.getCreditEarn();
  }

  isCreditEarnDisabled(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      return this.tokenStorageService.checkUserLoggedIn();
    }
    // SSR: no browser session — same falsy behaviour as before.
    return false;
  }

  contactUs(body: Record<string, unknown>): Observable<ResponseModel> {
    return this.http.post<ResponseModel>('contact-us', body).pipe(
      map((response) => response.data as ResponseModel),
      catchError((err: unknown) => {
        return throwError(() => err);
      })
    );
  }

  updateProfile(requestModel: UserModel): Observable<UserModel> {
    return this.http
      .patch<ResponseModel>(ApiConstants.USER_UPDATE_DETAIL_URL, requestModel)
      .pipe(
        map((response) => response.data as UserModel),
        catchError((err: unknown) => {
          return throwError(() => err);
        })
      );
  }

  updateProfilePicture(requestModel: FormData | Record<string, unknown>): Observable<UserModel> {
    return this.http
      .post<ResponseModel>(ApiConstants.USER_UPDATE_PROFILE_PICTURE_URL, requestModel)
      .pipe(
        map((response) => response.data as UserModel), // Map the API response
        catchError((err: unknown) => {
          this.toastService.showError(err);
          return throwError(() => err); // Pass the error to the caller
        })
      );
  }

  /**
   * Verify newsletter subscription token
   * @param formData - Object containing the subscription token
   * @returns Observable with verification response
   */
  verifyNewsletterSubscription(formData: Record<string, unknown>): Observable<ResponseModel> {
    return this.http.post<ResponseModel>(ApiConstants.NEWSLETTER_VERIFY_SUBSCRIPTION_URL, formData)
      .pipe(
        map((response) => response.data as ResponseModel),
        catchError((err: unknown) => {
          return throwError(() => err);
        })
      );
  }
}
